/**
 * Subscriptions Hook
 *
 * TanStack Query-backed hook for the org-level subscription list on the
 * /billing page. Backed by `['billing', 'subscriptions', { page }]`. Gated on
 * auth readiness so the hook does not fire during the cold-load window
 * (mirrors `useDomains`). The app-wide `refetchOnWindowFocus` default keeps
 * the list fresh after a Stripe-portal round trip in another tab.
 */

import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useAuth } from '../auth/auth0-provider';
import { listSubscriptions, type SubscriptionRow } from '@/api/billingApi';
import type { Paginated } from '@/api/pagination';

export function useSubscriptions(page: number = 1) {
  const { isAuthenticated, isSdkReady } = useAuth();

  return useQuery<Paginated<SubscriptionRow>>({
    queryKey: ['billing', 'subscriptions', { page }],
    queryFn: () => listSubscriptions({ page }),
    enabled: isAuthenticated && isSdkReady,
    placeholderData: keepPreviousData,
  });
}
